import React, { useCallback, useEffect, useState } from 'react';
import { Modal, BlockStack, InlineStack, Text, Badge, TextField, Select, Button, Banner, Spinner } from '@shopify/polaris';
import { statusTone, fmtAgo, newOpId } from './overstockHelpers';

const RETIRE_REASONS = [
  { label: 'Empty', value: 'empty' },
  { label: 'Damaged', value: 'damaged' },
  { label: 'Relabelled', value: 'relabelled' },
  { label: 'Lost', value: 'lost' },
];

function payloadSummary(ev) {
  const p = ev.payload || {};
  if (ev.event_type === 'rfid_box_qty_set') return `${p.qty_before ?? '?'} → ${p.qty_after ?? '?'}`;
  if (ev.event_type === 'rfid_box_shelved' || ev.event_type === 'rfid_box_moved') {
    return `to ${p.code_after || p.location_after || '?'}${p.location_before ? ` (from location ${p.location_before})` : ''}`;
  }
  if (ev.event_type === 'rfid_box_found') return `found${p.location_after ? ` at location ${p.location_after}` : ''}`;
  if (ev.event_type === 'rfid_box_created') return 'created';
  if (ev.event_type === 'rfid_box_epc_written') return 'EPC written to tag';
  if (ev.event_type === 'rfid_box_retired') return p.reason || '';
  if (ev.event_type === 'rfid_overstock_rollup') return `${p.value_before || '∅'} → ${p.value_after || '∅'}`;
  if (ev.event_type === 'rfid_box_assigned') return p.tid ? `TID ${p.tid}` : '';
  if (ev.event_type === 'rfid_count_missing') return `count run #${p.run_id}`;
  return '';
}

export default function BoxDetailModal({ api, boxId, onClose, onChanged, toast }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState('empty');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const j = await api(`/api/rfid/boxes/${boxId}`);
      setData(j);
      setQty(j.box.qty_units == null ? '' : String(j.box.qty_units));
    } catch (e) { setError(e.message); }
  }, [api, boxId]);

  useEffect(() => { if (boxId) load(); }, [boxId, load]);

  const run = async (label, fn) => {
    setBusy(label);
    try { await fn(); toast('ok', `${label} done`); await load(); onChanged?.(); }
    catch (e) { toast('err', e.message); }
    finally { setBusy(''); }
  };

  const box = data?.box;
  const live = box && box.status !== 'retired';

  return (
    <Modal open={!!boxId} onClose={onClose} title={box ? `Box #${box.id} · ${box.sku}` : `Box #${boxId}`} size="large">
      <Modal.Section>
        {error && <Banner tone="critical">{error}</Banner>}
        {!data && !error && <InlineStack align="center"><Spinner size="small" /></InlineStack>}
        {box && (
          <BlockStack gap="400">
            <BlockStack gap="100">
              <Text as="p" variant="headingSm">{box.title}</Text>
              <InlineStack gap="300">
                <Badge tone={statusTone(box.status)}>{box.status}</Badge>
                <Text as="span">Location: <b>{box.location_code || '—'}</b></Text>
                <Text as="span">Qty: <b>{box.qty_units ?? '—'}</b></Text>
                <Text as="span">Last seen: {fmtAgo(box.last_seen_at)}</Text>
              </InlineStack>
              <Text as="p" tone="subdued" className="overstock-mono">
                EPC {box.epc} · TID {box.tid || '—'} · {box.epc_written ? 'EPC written' : 'EPC not yet written'}
              </Text>
              <Text as="p" tone="subdued">
                Assigned by {box.assigned_by || '—'} · shelved by {box.shelved_by || '—'}{box.retired_by ? ` · retired by ${box.retired_by}` : ''}
              </Text>
            </BlockStack>

            {live && (
              <InlineStack gap="300" blockAlign="end" wrap>
                <TextField label="Units in box" type="number" min={0} value={qty} onChange={setQty} autoComplete="off" />
                <Button
                  loading={busy === 'Quantity'}
                  disabled={qty === '' || !Number.isFinite(Number(qty)) || Number(qty) === box.qty_units || (!!busy && busy !== 'Quantity')}
                  onClick={() => run('Quantity', () => api(`/api/rfid/boxes/${box.id}/qty`, { method: 'PUT', body: { qty_units: Number(qty), client_op_id: newOpId() } }))}
                >
                  Save qty
                </Button>
                <Select label="Retire reason" options={RETIRE_REASONS} value={reason} onChange={setReason} />
                <Button
                  tone="critical"
                  loading={busy === 'Retire'}
                  disabled={!!busy && busy !== 'Retire'}
                  onClick={() => run('Retire', () => api(`/api/rfid/boxes/${box.id}/retire`, { method: 'POST', body: { reason, client_op_id: newOpId() } }))}
                >
                  Retire box
                </Button>
                <Button
                  loading={busy === 'Resync'}
                  disabled={!!busy && busy !== 'Resync'}
                  onClick={() => run('Resync', () => api(`/api/rfid/boxes/${box.id}/resync`, { method: 'POST' }))}
                >
                  Resync overstock
                </Button>
              </InlineStack>
            )}

            <BlockStack gap="100">
              <Text as="p" variant="headingSm">History</Text>
              <div className="overstock-history">
                {(data.history || []).length === 0 && <Text as="p" tone="subdued">No events yet.</Text>}
                {(data.history || []).map((ev, i) => (
                  <div className="overstock-history-row" key={i}>
                    <span>{new Date(ev.created_at).toLocaleString()}</span>
                    <span><b>{ev.event_type.replace(/^rfid_/, '')}</b> {payloadSummary(ev)}</span>
                    <span>{ev.portal_username || ''}</span>
                  </div>
                ))}
              </div>
            </BlockStack>
          </BlockStack>
        )}
      </Modal.Section>
    </Modal>
  );
}
